import React, { useEffect, useMemo } from 'react';
import { Hash, CheckCircle2, Target, Trophy, TrendingUp } from 'lucide-react';
import { PracticeResults } from '../types';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { playEpicFanfare, playCorrectSound } from '../../FluencyDrill/sounds';

interface ResultScreenProps {
  results: PracticeResults;
  returnTo?: string;
  bestCQPM?: number | null;
}

const ResultScreen: React.FC<ResultScreenProps> = ({ results, returnTo = 'dashboard', bestCQPM }) => {
  const navigate = useNavigate();

  const passed = results.accuracy >= 90;
  const isNewBest = passed && (bestCQPM === null || bestCQPM === undefined || results.cqpm > bestCQPM);

  useEffect(() => {
    if (!passed) return;

    if (isNewBest) {
      // Big celebration for new high score
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.5 } });
      setTimeout(() => {
        confetti({ particleCount: 100, spread: 120, origin: { y: 0.5 } });
      }, 200);
      setTimeout(() => {
        confetti({ particleCount: 80, spread: 140, origin: { y: 0.6 } });
      }, 500);
      playEpicFanfare();
    } else {
      // Standard celebration for passing
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setTimeout(() => {
        confetti({ particleCount: 60, spread: 100, origin: { y: 0.6 } });
      }, 300);
      playCorrectSound();
    }
  }, [passed, isNewBest]);

  const formatAccuracy = (accuracy: number): string => {
    return Math.round(accuracy).toString();
  };

  const handleContinue = () => {
    sessionStorage.removeItem('currentAssessmentId');
    localStorage.removeItem('dailyPractice_date');
    localStorage.removeItem('dailyPractice_learning');
    localStorage.removeItem('dailyPractice_accuracy');
    localStorage.removeItem('dailyPractice_fluency');

    if (returnTo === 'onboarding') {
      navigate('/onboarding');
    } else {
      navigate('/');
    }
  };

  // Headline and message based on result
  const headline = useMemo(() => {
    if (isNewBest) return 'New High Score!';
    if (passed) return 'Great Job!';
    return 'Keep Practicing!';
  }, [isNewBest, passed]);

  const subMessage = useMemo(() => {
    if (isNewBest && bestCQPM !== null && bestCQPM !== undefined) return `You beat your best of ${bestCQPM}!`;
    if (isNewBest) return 'You set your first high score!';
    if (passed) return 'Assessment complete';
    return 'You need 90% accuracy to pass';
  }, [isNewBest, passed, bestCQPM]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 flex flex-col justify-center p-6">
      <div className="max-w-md mx-auto space-y-6">

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="text-center"
        >
          {isNewBest && (
            <motion.div
              initial={{ rotate: -10, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 300 }}
            >
              <Trophy className="w-14 h-14 text-yellow-500 mx-auto mb-2" />
            </motion.div>
          )}
          <h1 className={`text-4xl font-black ${isNewBest
            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-transparent bg-clip-text'
            : passed
              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-transparent bg-clip-text'
              : 'text-gray-500'
          }`}>
            {headline}
          </h1>
          <p className="text-base text-indigo-400 font-medium mt-1">
            {subMessage}
          </p>
        </motion.div>

        {/* Score Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="relative overflow-hidden rounded-2xl p-6 text-center bg-white border border-indigo-100 shadow-lg shadow-indigo-100/50"
        >
          <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-bl from-indigo-50 to-transparent rounded-bl-full" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-purple-50 to-transparent rounded-tr-full" />

          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">
              Speed Score
            </p>
            <span className={`block text-7xl font-black leading-tight ${isNewBest
              ? 'bg-gradient-to-b from-yellow-500 to-orange-500 text-transparent bg-clip-text'
              : 'bg-gradient-to-b from-indigo-500 to-purple-600 text-transparent bg-clip-text'
            }`}>
              {results.cqpm}
            </span>

            {/* Best score comparison */}
            {passed && bestCQPM !== null && bestCQPM !== undefined && !isNewBest && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center justify-center gap-1.5 mt-2 text-sm text-indigo-400"
              >
                <TrendingUp className="w-4 h-4" />
                <span>Best: {bestCQPM}</span>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-xl border border-indigo-100 p-4 text-center shadow-sm"
          >
            <Hash className="w-4 h-4 text-indigo-400 mx-auto mb-1" />
            <span className="block text-2xl font-bold text-indigo-600">
              {results.totalAttempted}
            </span>
            <span className="text-xs text-indigo-300 font-medium">Attempted</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border border-indigo-100 p-4 text-center shadow-sm"
          >
            <CheckCircle2 className="w-4 h-4 text-indigo-400 mx-auto mb-1" />
            <span className="block text-2xl font-bold text-indigo-600">
              {results.correctAnswers}
            </span>
            <span className="text-xs text-indigo-300 font-medium">Correct</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-xl border border-indigo-100 p-4 text-center shadow-sm"
          >
            <Target className="w-4 h-4 text-indigo-400 mx-auto mb-1" />
            <span className="block text-2xl font-bold text-indigo-600">
              {formatAccuracy(results.accuracy)}%
            </span>
            <span className="text-xs text-indigo-300 font-medium">Accuracy</span>
          </motion.div>
        </div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="pt-1"
        >
          <motion.button
            whileHover={{ scale: 1.03, boxShadow: '0 10px 40px -10px rgba(99, 102, 241, 0.5)' }}
            whileTap={{ scale: 0.97 }}
            onClick={handleContinue}
            className="w-full px-8 py-5 bg-gradient-to-r from-indigo-500 to-purple-500
              text-white rounded-2xl font-bold text-xl shadow-lg shadow-indigo-200/50
              transition-all duration-300"
          >
            Continue
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultScreen;
